import {
  createMatterPinnedV1,
  deleteMatterPinnedV1,
  retrievePinnedMattersV2,
  type CreateMatterPinnedRequest,
} from "@/lib/data/requests";
import { queryKeys } from "@/lib/data/static/query-keys";
import { type APIError } from "@/lib/types";
import { toastAPIErrorMessage } from "@/lib/utils/axiosUtils";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { type AxiosError } from "axios";
import toast from "react-hot-toast";

export const useCreateMatterPinned = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: CreateMatterPinnedRequest) => createMatterPinnedV1(data),
    onError: (error: AxiosError<APIError>) => {
      toastAPIErrorMessage(error, "Error pinning matter");
    },
    onSuccess: () => {
      toast.success("Matter pinned");
    },
    onSettled: async (_data, _error, variables) => {
      await queryClient.invalidateQueries({
        queryKey: [queryKeys.matters],
      });
      await queryClient.invalidateQueries({
        queryKey: [queryKeys.pinnedMatters],
      });

      if (variables.matterId) {
        await queryClient.invalidateQueries({
          queryKey: [queryKeys.matters, variables.matterId],
        });
      }
    },
  });
};

export const useDeleteMatterPinned = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: Parameters<typeof deleteMatterPinnedV1>[0]) =>
      deleteMatterPinnedV1(data),
    onError: (error: AxiosError<APIError>) => {
      toastAPIErrorMessage(error, "Error unpinning matter");
    },
    onSuccess: () => {
      toast.success("Matter unpinned");
    },
    onSettled: async () => {
      await queryClient.invalidateQueries({
        queryKey: [queryKeys.matters],
      });
      await queryClient.invalidateQueries({
        queryKey: [queryKeys.pinnedMatters],
      });
    },
  });
};

export const retrievePinnedMattersQueryOptions = () => ({
  queryKey: [queryKeys.pinnedMatters],
  queryFn: async () => retrievePinnedMattersV2(),
});
